import { type ReactNode, useEffect, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { identify, track } from '@repo/shared/client';
import { currentOrganizationQueryOptions } from '~/lib/queries/organization';
import { resolveLandingRoute } from '~/lib/landing';
import { LoadingOverlay } from '~/components/ui';

export const Route = createFileRoute('/_authed/post-sign-in')({
  loader: async ({ context }) => {
    // Resolve + cache the caller's active organization (`/api/organizations/current`
    // → `resolveActiveOrganization`, which re-verifies membership and never
    // auto-creates an org) so the component can fork by onboarding state from a
    // warm cache. `ensureQueryData` drives the isomorphic fetcher (in-process on
    // the server; `apiClient` on a client navigation).
    //
    // We swallow failures: a slow/failed resolve must never trap the user on this
    // screen — the component routes on regardless (a missing org reads as "no org"
    // and sends them to create one).
    await context.queryClient
      .ensureQueryData(currentOrganizationQueryOptions())
      .catch(() => undefined);
  },
  component: PostSignInRoute,
});

/**
 * Post-sign-in landing route — the destination after authentication (both
 * email/password and Google land here). The loader resolves + seeds the caller's
 * organization; here we fire sign-in analytics once and then fork by onboarding
 * state: an unverified email goes to verify-email, a verified user with no org to
 * create-organization, and a fully-onboarded user into the app at their landing
 * surface (Modeling, or Uploads when the org has no loans yet).
 */
function PostSignInRoute(): ReactNode {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // `_authed` gated auth in beforeLoad and returned the session onto the context.
  const { session } = Route.useRouteContext();
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    identify(session.user.id);
    // This shared landing is reached after both email/password and Google
    // sign-in, so the method isn't known here. For per-method attribution,
    // fire `user_signed_in` at each entry point instead.
    track('user_signed_in', { method: 'unknown' });

    // Route by onboarding state. The org was resolved + cached by the loader
    // (best-effort), so read it from the cache rather than refetching; treat a
    // missing/failed resolve as "no org" and send the user to create one. A
    // fully-onboarded user lands via `resolveLandingRoute` (Modeling, or Uploads
    // when the org has no loans yet).
    async function routeOnward(): Promise<void> {
      if (!session.user.emailVerified) {
        void navigate({ to: '/onboarding/verify-email', replace: true });
        return;
      }
      const hasOrg = Boolean(
        queryClient.getQueryData(currentOrganizationQueryOptions().queryKey)?.organization,
      );
      const to = hasOrg
        ? await resolveLandingRoute(queryClient)
        : '/onboarding/create-organization';
      void navigate({ to, replace: true });
    }

    void routeOnward();
  }, [session.user.id, session.user.emailVerified, queryClient, navigate]);

  return <LoadingOverlay visible />;
}
